import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';

import './menu.css';

import Menu, {Item as MenuItem, SubMenu} from 'rc-menu';

import {faAngleDown, faTimes} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {MenuMode} from 'rc-menu/lib/interface';
import ApplicationContext from '../../ApplicationContext';

export interface IfmsMenuItem {
  menuId?: string | null,
  selected: boolean,
  items?: Array<IfmsMenuItem> | undefined,
  separator?: boolean,
  text: React.ReactNode | string | null | undefined,
  index?: number | undefined,
  job?: any | null, // Job Switching Tab item can be attached to the menu item
  variant?: 'plain' | 'highlight',
  disabled?:boolean,
  icon?: React.ReactNode|null,
  icon2?: React.ReactNode|null,
  url?:string,
  name?:string,
};

interface MenuItemMap {
  [indexer: string]: IfmsMenuItem
};

type IfmsMenuProps = {
  onMenuItemSelected: (menuItem: IfmsMenuItem, e: React.MouseEvent<HTMLElement>) => void,
  mode: MenuMode,
  items: IfmsMenuItem[][][],
  specialMenu?: string | undefined,
  menuStyle: (i: number) => React.CSSProperties,
  subMenuStyle?: React.CSSProperties,
  menuWidth?: string | undefined,
  variant?: 'plain' | 'highlight',
  dismiss?: VoidFunction,
};

type MenuItemFactoryProps = {
  item: IfmsMenuItem,
  customSubmenuArrow: boolean,
  style: React.CSSProperties,
  variant?: 'plain' | 'highlight',
  disabled?:boolean,
  icon?: React.ReactNode|null,
  specialMenu:string,
  name?:string,
  useDarkMode?: boolean|undefined,
};

const MenuItemFactory: React.FC<MenuItemFactoryProps> = ({ item, customSubmenuArrow, style, variant, specialMenu, useDarkMode, ...other }) => {
  if (!item.items || item.items.length <= 0) {
    let menuItemClasses = item.separator ? 'ifms-custom-menu-item-highlight ifms-custom-menu-item-separator' : 'ifms-custom-menu-item-highlight';
    if (variant === 'plain') {
      if (specialMenu === 'user') menuItemClasses = 'ifms-custom-usermenu-item-plain';
      else if (specialMenu === 'jobswitchinghelper') menuItemClasses = !useDarkMode ? 'ifms-custom-jobswitchinghelpermenu-item-plain' : 'ifms-custom-jobswitchinghelpermenu-item-darkmode-plain';
      else if (specialMenu === 'tooltip') menuItemClasses = 'ifms-custom-tooltipmenu-item-plain';
      else menuItemClasses =  !useDarkMode ? 'ifms-custom-menu-item-plain' : 'ifms-custom-menu-item-darkmode-plain';
    }
    if (item.icon && item.icon2) {
      return <MenuItem className={menuItemClasses} disabled={item?.disabled ?? false} key={item.menuId} style={style} name={item.name} {...other}> {item.icon}&nbsp;{item.text}&nbsp;&nbsp;{item.icon2} </MenuItem>
    } else {
      return (
        <>
          {
            item.icon
              ?
              <MenuItem className={menuItemClasses} disabled={item?.disabled ?? false} key={item.menuId} style={style} name={item.name} {...other}> {item.icon}&nbsp;{item.text}&nbsp;{item.icon2} </MenuItem>
              :
              <MenuItem className={menuItemClasses} disabled={item?.disabled ?? false} key={item.menuId} style={style} name={item.name} {...other}> {item.text} </MenuItem>
          }
        </>
      );
    }
  }
  const submenuContent = customSubmenuArrow
    ? <span>{item.text} <FontAwesomeIcon icon={faAngleDown}/></span>
    : <span>{item.text}</span>;

  return (
    <SubMenu title={submenuContent} {...other}>
      {item.items.map(si => (
        <MenuItemFactory item={si} key={si.menuId} customSubmenuArrow={false} style={style} specialMenu={specialMenu}/>
      ))}
    </SubMenu>
  );
}

function withMenuId(items: Array<IfmsMenuItem>, menuItemMap: MenuItemMap, selectedKeys: Array<string>): Array<IfmsMenuItem> {
  return items.map(x => {
    const menuId = _.uniqueId();
    if (x.selected) {
      selectedKeys.push(menuId);
    }

    const result: IfmsMenuItem = {
      ...x,
      menuId,
      items: x.items ? withMenuId(x.items, menuItemMap, []) : undefined,
    };

    menuItemMap[menuId] = result;

    return result;
  });

}

const IfmsMenu: React.FC<IfmsMenuProps> = ({
                                             items,
                                             specialMenu,
                                             onMenuItemSelected,
                                             mode,
                                             menuStyle,
                                             subMenuStyle,
                                             variant,
                                             menuWidth = undefined,
                                             dismiss = undefined,
                                             ...other
                                           }) => {
  const appContext = useContext(ApplicationContext); 
  const menuItemMap: MenuItemMap = {};
  const selectedKeys: Array<string> = [];
  const menuItems = items.map(subItems => subItems.map((subSubItems) => withMenuId(subSubItems, menuItemMap, selectedKeys)))
  const useDarkMode = appContext.useDarkMode;

  let totalGroups = -1;

  // let className = '';
  // if (specialMenu === 'user')
  //   className = `ifms-custom-usermenu-${variant}`;
  // else if (specialMenu === 'jobswitchinghelper')
  //   className = !useDarkMode ? `ifms-custom-jobswitchinghelpermenu-${variant}` : `ifms-custom-jobswitchinghelpermenu-darkmode-${variant}`;
  //   else if (specialMenu === 'tooltip')
  //   className = `ifms-custom-tooltipmenu-${variant}`;
  // else {
  //   className = !useDarkMode ? `ifms-custom-menu-${variant}` : `ifms-custom-menu-darkmode-${variant}`;
  // }

  // // let subMenuClassName = !useDarkMode || specialMenu === 'tooltip' ? `ifms-custom-menu-submenu-${variant}` : `ifms-custom-menu-submenu-darkmode-${variant}`
  // let subMenuClassName = `ifms-custom-menu-submenu-darkmode-${variant}`;
  // if (specialMenu === 'user') {
  //   subMenuClassName = `ifms-custom-usermenu-submenu-${variant}`;
  // } else if (!useDarkMode || specialMenu === 'tooltip') {
  //   subMenuClassName = `ifms-custom-menu-submenu-${variant}`;
  // } 
  
  let className = !useDarkMode ? `ifms-custom-menu-${variant}` : `ifms-custom-menu-darkmode-${variant}`;
  let subMenuClassName = !useDarkMode || specialMenu === 'tooltip' ? `ifms-custom-menu-submenu-${variant}` : `ifms-custom-menu-submenu-darkmode-${variant}`;
  
  switch (specialMenu) {
    case 'user':
      className = `ifms-custom-usermenu-${variant}`;
      subMenuClassName = !useDarkMode ? `ifms-custom-usermenu-submenu-${variant}` : `ifms-custom-menu-submenu-darkmode-${variant}`;
      break;
    case 'jobswitchinghelper':
      className = !useDarkMode ? `ifms-custom-jobswitchinghelpermenu-${variant}` : `ifms-custom-jobswitchinghelpermenu-darkmode-${variant}`;
      subMenuClassName = "";
      break;
    case 'tooltip':
      className = `ifms-custom-tooltipmenu-${variant}`;
      break;
    case 'rightClickContext':
      className = !useDarkMode ? 'ifms-custom-rightclickmenu' : 'ifms-custom-rightclickmenu-darkmode';
      break;
  }

  function procesMenuSelection(e: any) {
    const menuKey: string = e.key as string;
    onMenuItemSelected(menuItemMap[menuKey], e.domEvent ? e.domEvent : e);
  }

  function MenuItems() {
    return (
      <>
        {menuItems.map((subMenuItem, ind) => {
          return (
            <div className={subMenuClassName} key={`ifms-custom-menu-submenu-${ind}`} >
              {subMenuItem.map((menuItem, i) => {
                totalGroups += 1;
                return (
                  <Menu key={i} mode={mode} onClick={procesMenuSelection} style={menuStyle(totalGroups)}
                        selectedKeys={selectedKeys} {...other}>
                    {menuItem.map(subMenuItem => <MenuItemFactory item={subMenuItem} key={subMenuItem.menuId} useDarkMode={useDarkMode}
                                                                  customSubmenuArrow disabled={subMenuItem?.disabled ?? false}
                      style={subMenuStyle || {}} variant={variant} specialMenu={specialMenu ?? ''} />)}
                  </Menu>
                )
              })}
            </div>
          )
        })}
      </>
    )
  }

  if (specialMenu === 'rightClickContext') {
    return (
      <div className={className}>
        <div style={{ display:'flex', justifyContent:'flex-end', }}>
          <span className='ifms-custom-munu-close' 
            onClick={()=>{ if (dismiss) dismiss()}}
            >
            <FontAwesomeIcon id='ifms-modal-close-x-icon' style={{color: useDarkMode ? 'white' : 'black' }} icon={faTimes} />
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '15px 60px 40px 60px' }} >
          {MenuItems()}
        </div>
      </div>
    );
  } else {
    return (
      <div className={className} style={{display: 'flex', justifyContent: 'space-between', width: menuWidth}} >
        {MenuItems()}
      </div>
    )
  }
};

IfmsMenu.defaultProps = {
  onMenuItemSelected: () => {
  },
  items: [],
  mode: 'horizontal',
  variant: 'plain',
};

IfmsMenu.propTypes = {
  onMenuItemSelected: PropTypes.func.isRequired,
  items: PropTypes.array.isRequired,
  mode: PropTypes.oneOf(['horizontal', 'vertical'] as const).isRequired,
  subMenuStyle: PropTypes.object,
  variant: PropTypes.oneOf(['plain', 'highlight'] as const).isRequired,
};


export default React.memo(IfmsMenu);